import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { CATEGORIES } from "../data/courses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "General Enquiry",
  ...CATEGORIES.flatMap((c) => [`${c.name} — not sure yet`, ...[]]),
  "Network Security",
  "Cloud Security",
  "Cyber Security",
  "Career Guidance / Free Demo",
];

export const ContactForm = ({ compact = false, defaultInterest = "General Enquiry", idPrefix = "contact" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: defaultInterest, message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "website" });
      toast.success("Enquiry sent! Our counsellor will reach out within a few hours.");
      setForm({ name: "", email: "", phone: "", interest: defaultInterest, message: "" });
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp instead — we reply instantly there.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-[#0F1A36] border border-sky-500/20 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-orange-500/70 focus:ring-1 focus:ring-orange-500/40 transition-colors duration-200";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${idPrefix}-form`}>
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <input
          data-testid={`${idPrefix}-name-input`}
          required
          minLength={2}
          value={form.name}
          onChange={set("name")}
          placeholder="Your full name"
          className={inputCls}
        />
        <input
          data-testid={`${idPrefix}-phone-input`}
          required
          minLength={6}
          value={form.phone}
          onChange={set("phone")}
          placeholder="Phone / WhatsApp number"
          className={inputCls}
        />
      </div>
      <input
        data-testid={`${idPrefix}-email-input`}
        required
        type="email"
        value={form.email}
        onChange={set("email")}
        placeholder="Email address"
        className={inputCls}
      />
      <select
        data-testid={`${idPrefix}-interest-select`}
        value={form.interest}
        onChange={set("interest")}
        className={`${inputCls} appearance-none cursor-pointer`}
      >
        {INTERESTS.map((i) => (
          <option key={i} value={i} className="bg-[#0F1A36]">
            {i}
          </option>
        ))}
      </select>
      {!compact && (
        <textarea
          data-testid={`${idPrefix}-message-input`}
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us your goal — e.g. crack a TAC interview, switch to cloud security..."
          className={`${inputCls} resize-none`}
        />
      )}
      <button
        data-testid={`${idPrefix}-submit-btn`}
        type="submit"
        disabled={loading}
        className="group w-full inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-display font-semibold text-sm px-6 py-3.5 transition-colors duration-200 shadow-[0_0_30px_rgba(249,115,22,0.25)] hover:shadow-[0_0_40px_rgba(249,115,22,0.4)]"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
        {loading ? "Sending..." : "Book My Free 1-on-1 Demo"}
      </button>
      <p className="text-xs text-slate-500 text-center">Free demo session • No spam, ever • Reply within 2–4 hours</p>
    </form>
  );
};
